"use client"

import type React from "react"
import { useState, useEffect, useCallback, useRef } from "react"
import BackIcon from "../../components/back-icon"
import UpperNav from "../../components/upper-nav"
import SpecialtyList from "./SpecialtyList"
import SearchForm from "./SearchForm"

function DoctorsList() {
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useState<{ doctorName: string; location: string } | null>(null)
  const [doctors, setDoctors] = useState<any[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)

  // Refs to track previous values
  const prevDoctorsRef = useRef<any[]>([])
  const prevTotalCountRef = useRef<number>(0)
  const prevTotalPagesRef = useRef<number>(0)
  const prevCurrentPageRef = useRef<number>(0)
  const prevIsLoadingRef = useRef<boolean>(false)
  const prevIsErrorRef = useRef<boolean>(false)

  const handleSelectSpecialty = useCallback((specialty: string): void => {
    setSelectedSpecialty(specialty)
    setSearchParams(null)
    setDoctors([])
    setTotalCount(0)
    setTotalPages(0)
    setCurrentPage(0)
    setIsLoading(false)
    setIsError(false)
  }, [])

  const handleSearch = useCallback(
    ({
      doctorName,
      location,
      doctors,
      totalItems,
      totalPages,
      isLoading,
      isError,
      currentPage: searchFormPage,
      setCurrentPage: setSearchFormPage,
    }: {
      doctorName: string
      location: string
      doctors: any[]
      totalItems: number
      totalPages: number
      isLoading: boolean
      isError: boolean
      currentPage: number
      setCurrentPage: React.Dispatch<React.SetStateAction<number>>
    }): void => {
      setSearchParams((prev) => {
        // Only update if values have changed
        if (
          prev?.doctorName !== doctorName ||
          prev?.location !== location ||
          doctors !== prevDoctorsRef.current ||
          totalItems !== prevTotalCountRef.current ||
          totalPages !== prevTotalPagesRef.current ||
          searchFormPage !== prevCurrentPageRef.current ||
          isLoading !== prevIsLoadingRef.current ||
          isError !== prevIsErrorRef.current
        ) {
          prevDoctorsRef.current = doctors
          prevTotalCountRef.current = totalItems
          prevTotalPagesRef.current = totalPages
          prevCurrentPageRef.current = searchFormPage
          prevIsLoadingRef.current = isLoading
          prevIsErrorRef.current = isError
          setDoctors(doctors)
          setTotalCount(totalItems)
          setTotalPages(totalPages)
          setCurrentPage(searchFormPage)
          setIsLoading(isLoading)
          setIsError(isError)
          return { doctorName, location }
        }
        return prev
      })
    },
    [],
  )

  const handleBack = useCallback((): void => {
      
      setSelectedSpecialty(null)
  }, [searchParams, ])

  const removeFilter = useCallback(
    (filter: "specialty" | "location" | "doctorName") => {
      if (filter === "specialty") {
        setSelectedSpecialty(null)
        setSearchParams(null)
      } else if (filter === "location" && searchParams) {
        setSearchParams({ ...searchParams, location: "" })
      } else if (filter === "doctorName" && searchParams) {
        setSearchParams({ ...searchParams, doctorName: "" })
      }
      setCurrentPage(0)
      setDoctors([])
      setTotalCount(0)
      setTotalPages(0)
      setIsLoading(false)
      setIsError(false)
    },
    [searchParams],
  )

  // Reset loading state when no search is active
  useEffect(() => {
    if (!searchParams) {
      setIsLoading(false)
      setDoctors([])
      setTotalCount(0)
      setTotalPages(0)
      setIsError(false)
    }
  }, [searchParams])

  return (
    <>
      <UpperNav />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">

        {selectedSpecialty && (
          <div className="bg-white">
            <div className="w-full px-0 sm:px-0 lg:px-0 pt-20 sm:pt-20 md:pt-4 lg:pt-6">
              <div className="w-full">
                <div className="flex items-center gap-3 md:gap-4 px-4 sm:px-6 lg:px-4 lg:mb-2">
                  <button
                    onClick={handleBack}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 group"
                  >
                    <BackIcon />
                  </button>
                  <div className="flex flex-col md:flex-row md:items-center md:gap-4 w-full">
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                      {searchParams ? "Search Results" : "Search for a Doctor"}
                    </h3>
                    {selectedSpecialty && (
                      <div className="flex items-center gap-2 mt-1 md:mt-0">
                        <span className="text-sm text-gray-500">Specialty:</span>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#E6F0FA] text-[#0F676C] border border-[#C5D9E8]">
                          {selectedSpecialty}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className={selectedSpecialty ? "" : "pt-6 sm:pt-6"}>
          {!selectedSpecialty && <SpecialtyList onSelectSpecialty={handleSelectSpecialty} />}
          {selectedSpecialty && <SearchForm selectedSpecialty={selectedSpecialty} onSearch={handleSearch} />}
        </div>
      </div>
    </>
  )
}

export default DoctorsList
